'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

interface ScreenDimensionContextType {
  screenWidth: number
  screenHeight: number
}

const DEFAULT_DIMENSIONS: ScreenDimensionContextType = {
  screenWidth: 0,
  screenHeight: 0,
}

const ScreenDimensionContext = createContext<ScreenDimensionContextType>(DEFAULT_DIMENSIONS)

let _currentDimensions: ScreenDimensionContextType = DEFAULT_DIMENSIONS

interface ScreenDimensionProviderProps {
  children: React.ReactNode
  debounceTime?: number
}

export const ScreenDimensionProvider = ({ children, debounceTime = 300 }: ScreenDimensionProviderProps) => {
  const [dimensions, setDimensions] = useState<ScreenDimensionContextType>({
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    screenHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
  })

  const handleResize = useCallback(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      timeoutId = setTimeout(() => {
        const newDimensions = {
          screenWidth: window.innerWidth,
          screenHeight: window.innerHeight,
        }

        setDimensions(newDimensions)
        _currentDimensions = newDimensions
      }, debounceTime)
    }
  }, [debounceTime])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const initialDimensions = {
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
      }

      setDimensions(initialDimensions)
      _currentDimensions = initialDimensions

      const debouncedResizeHandler = handleResize()
      window.addEventListener('resize', debouncedResizeHandler)

      return () => {
        window.removeEventListener('resize', debouncedResizeHandler)
      }
    }

    return () => {}
  }, [handleResize])

  return <ScreenDimensionContext.Provider value={dimensions}>{children}</ScreenDimensionContext.Provider>
}

export const useScreenDimensions = () => {
  const context = useContext(ScreenDimensionContext)

  if (context === undefined) {
    throw new Error('useScreenDimensions must be used within a ScreenDimensionProvider')
  }

  return context
}

export const getCurrentScreenDimensions = (): ScreenDimensionContextType => {
  if (typeof window === 'undefined') {
    return DEFAULT_DIMENSIONS
  }
  return _currentDimensions
}
